import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import './Checkout.css';

const formatPrice = (p) => '৳' + p.toLocaleString('en-BD');

const PAYMENT_METHODS = [
  { id: 'cod', label: 'Cash on Delivery', icon: '💵', available: true },
  { id: 'bkash', label: 'bKash', icon: '🔴', available: false },
  { id: 'card', label: 'Card (Visa / Mastercard)', icon: '💳', available: false },
];

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, totalPrice, clearCart } = useCart();

  const [form, setForm] = useState({
    name: '', phone: '', email: '', address: '', district: '', note: '',
  });
  const [payment, setPayment] = useState('cod');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  if (cart.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p>Your cart is empty.</p>
        <button onClick={() => navigate('/')}>Go Shopping</button>
      </div>
    );
  }

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.phone.trim()) e.phone = 'Phone is required';
    else if (!/^(\+880|880|0)[0-9]{10}$/.test(form.phone.replace(/\s/g, '')))
      e.phone = 'Enter a valid Bangladeshi phone number';
    if (!form.address.trim()) e.address = 'Address is required';
    if (!form.district.trim()) e.district = 'District is required';
    return e;
  };

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setErrors((err) => ({ ...err, [e.target.name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }

    setSubmitting(true);
    try {
      const orderItems = cart.map((item) => ({
        product_id: item.id,
        product_name: item.name,
        product_image: item.image,
        price: item.discount ? Math.round(item.price * (1 - item.discount / 100)) : item.price,
        quantity: item.qty,
      }));

      const payload = {
        customer_name: form.name,
        customer_phone: form.phone,
        customer_email: form.email,
        customer_address: form.address,
        customer_district: form.district,
        note: form.note,
        payment_method: payment,
        total_amount: totalPrice,
        items: orderItems,
      };

      const res = await fetch(`${API_BASE}/orders.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success) {
        clearCart();
        navigate('/order-success', { state: { orderId: data.order_id, phone: form.phone } });
      } else {
        alert(data.message || 'Failed to place order. Please try again.');
      }
    } catch (err) {
      alert('Network error. Please check your connection.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="co-page">
      <header className="co-header">
        <button className="co-back-btn" onClick={() => navigate(-1)}>← Back</button>
        <span className="co-header-title">Checkout</span>
      </header>

      <form className="co-content" onSubmit={handleSubmit} noValidate>
        {/* Left column */}
        <div className="co-left">

          {/* Delivery Info */}
          <section className="co-section">
            <div className="co-section-title">📦 Delivery Information</div>
            <div className="co-form-grid">
              <div className="co-field">
                <label>Full Name *</label>
                <input name="name" value={form.name} onChange={handleChange} placeholder="Your full name" />
                {errors.name && <span className="co-error">{errors.name}</span>}
              </div>
              <div className="co-field">
                <label>Phone Number *</label>
                <input name="phone" value={form.phone} onChange={handleChange} placeholder="01XXXXXXXXX" />
                {errors.phone && <span className="co-error">{errors.phone}</span>}
              </div>
              <div className="co-field co-field-full">
                <label>Email (optional)</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="email@example.com" />
              </div>
              <div className="co-field co-field-full">
                <label>Full Address *</label>
                <input name="address" value={form.address} onChange={handleChange} placeholder="House, Road, Area" />
                {errors.address && <span className="co-error">{errors.address}</span>}
              </div>
              <div className="co-field">
                <label>District *</label>
                <select name="district" value={form.district} onChange={handleChange}>
                  <option value="">Select District</option>
                  {['Dhaka','Chittagong','Sylhet','Rajshahi','Khulna','Barishal','Rangpur','Mymensingh',
                    'Comilla','Narsingdi','Gazipur','Narayanganj'].map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                {errors.district && <span className="co-error">{errors.district}</span>}
              </div>
              <div className="co-field co-field-full">
                <label>Order Note (optional)</label>
                <textarea name="note" value={form.note} onChange={handleChange}
                  placeholder="Any special instructions..." rows={2} />
              </div>
            </div>
          </section>

          {/* Payment */}
          <section className="co-section">
            <div className="co-section-title">💳 Payment Method</div>
            <div className="co-payment-list">
              {PAYMENT_METHODS.map((m) => (
                <label
                  key={m.id}
                  className={`co-payment-option ${payment === m.id ? 'selected' : ''} ${!m.available ? 'disabled' : ''}`}
                >
                  <input
                    type="radio"
                    name="payment"
                    value={m.id}
                    checked={payment === m.id}
                    onChange={() => m.available && setPayment(m.id)}
                    disabled={!m.available}
                  />
                  <span className="co-pay-icon">{m.icon}</span>
                  <span className="co-pay-label">{m.label}</span>
                  {!m.available && <span className="co-pay-soon">Coming Soon</span>}
                </label>
              ))}
            </div>
          </section>
        </div>

        {/* Right column — order summary */}
        <div className="co-right">
          <div className="co-summary">
            <div className="co-summary-title">Order Summary</div>
            <div className="co-summary-items">
              {cart.map((item) => {
                const p = item.discount ? Math.round(item.price * (1 - item.discount / 100)) : item.price;
                return (
                  <div className="co-summary-item" key={item.id}>
                    <img src={item.image} alt={item.name} />
                    <div>
                      <div className="co-summary-item-name">{item.name}</div>
                      <div className="co-summary-item-meta">Qty: {item.qty} × {formatPrice(p)}</div>
                    </div>
                    <div className="co-summary-item-sub">{formatPrice(p * item.qty)}</div>
                  </div>
                );
              })}
            </div>
            <div className="co-summary-divider" />
            <div className="co-summary-row"><span>Subtotal</span><span>{formatPrice(totalPrice)}</span></div>
            <div className="co-summary-row"><span>Delivery</span><span style={{color:'#16a34a',fontWeight:600}}>FREE</span></div>
            <div className="co-summary-divider" />
            <div className="co-summary-total"><span>Total</span><span>{formatPrice(totalPrice)}</span></div>

            <button type="submit" className="co-place-btn" disabled={submitting}>
              {submitting ? 'Placing Order...' : '✓ Place Order'}
            </button>
            <p className="co-secure-note">🔒 Safe & Secure Checkout</p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
